import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Chatbot from "../components/Chatbot";
import { fetchPublicPosts } from "../lib/api";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Insights · Greenroute";
    window.scrollTo(0, 0);
    fetchPublicPosts()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-cream" data-testid="blog-list-page">
      <Navbar />
      <section className="pt-40 pb-16 md:pb-24 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="uppercase text-xs tracking-[0.3em] text-gold mb-6">
            Greenroute Insights
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
            Notes from the field on estate maintenance.
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-graphite">Loading insights…</div>
          ) : posts.length === 0 ? (
            <div className="py-20 text-center" data-testid="blog-empty-state">
              <div className="font-serif text-3xl text-forest mb-3">No insights yet.</div>
              <p className="text-graphite max-w-md mx-auto">
                The first articles are being prepared. In the meantime, our team is one
                call away — +27 66 183 3893.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bordersoft border border-bordersoft" data-testid="blog-grid">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.08 }}
                  className="bg-cream group hover:bg-white transition-colors duration-500"
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="block p-8 md:p-10 h-full"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {p.cover_image && (
                      <div className="relative w-full h-48 mb-6 overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    )}
                    <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-4">
                      {new Date(p.created_at).toLocaleDateString("en-ZA", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl text-ink leading-tight mb-4">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="text-graphite text-sm leading-relaxed mb-6">
                        {p.excerpt}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-forest group-hover:text-gold transition-colors">
                      Read article <ArrowRight size={14} />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Chatbot />
    </main>
  );
}
